import type React from 'react';
import { useCallback } from 'react';
import apiService from '@/services/apiService';
import type { ApplicationData, ApplicationResponse } from '@/types/app';

export const DEFAULT_APPLICATION: ApplicationData = {
	quote_id: null,
	initials: null,
	prefix: null,
	lastName: null,
	email: null,
	phone: null,
	arboService: false,
	arbodienstverlener: null,
	agreeToTerms: null,
	agreeToCancel: null,
	hasNoLegalIssues: null,
	hasNoInsuranceIssues: null,
	hasNoBankruptcyIssues: null,
	selected_sick_leave_short_term_quote_id: null,
	selected_occupational_health_service_quote_id: null,
	selected_wga_gap_extended_quote_id: null,
	selected_wga_gap_quote_id: null,
	selected_wia_excess_quote_id: null
};

/**
 * Converts API response (snake_case) to frontend format (camelCase)
 */
function mapResponseToApplicationData(response: ApplicationResponse): Partial<ApplicationData> {
	const mappedData: Partial<ApplicationData> = {
		initials: response.initials ?? null,
		prefix: response.prefix ?? null,
		lastName: response.last_name ?? null,
		email: response.email ?? null,
		phone: response.phone ?? null,
		agreeToTerms: response.agree_to_terms ?? null,
		agreeToCancel: response.agree_to_cancel ?? null,
		hasNoLegalIssues: response.has_no_legal_issues ?? null,
		hasNoInsuranceIssues: response.has_no_insurance_issues ?? null,
		hasNoBankruptcyIssues: response.has_no_bankruptcy_issues ?? null,
		selected_sick_leave_short_term_quote_id:
			response.selected_sick_leave_short_term_quote_id ?? null,
		selected_occupational_health_service_quote_id:
			response.selected_occupational_health_service_quote_id ?? null,
		selected_wga_gap_extended_quote_id: response.selected_wga_gap_extended_quote_id ?? null,
		selected_wga_gap_quote_id: response.selected_wga_gap_quote_id ?? null,
		selected_wia_excess_quote_id: response.selected_wia_excess_quote_id ?? null
	};

	if (response.selected_wia_excess_dekkingspercentage !== undefined) {
		mappedData.selected_wia_excess_dekkingspercentage =
			response.selected_wia_excess_dekkingspercentage ?? null;
	}

	return mappedData;
}

export function useApplicationManagement(
	setApplicationData: React.Dispatch<React.SetStateAction<ApplicationData>>
) {
	const updateApplication = useCallback(
		(newData: Partial<ApplicationData>, submit = false) => {
			setApplicationData(
				(prevData) =>
					({
						...prevData,
						...newData,
						submitted: submit
					}) as ApplicationData
			);
		},
		[setApplicationData]
	);

	/**
	 * Fetches application data from the API and populates the form.
	 * Retrieves pre-filled contact details (name, email, phone) from the contact form.
	 * Most non-null values are merged into state so that API nulls (e.g. for fields
	 * not yet saved) don't overwrite values already set by the UI.
	 * However, quote selection fields are ALWAYS applied (even if null) because the
	 * backend clears them when quotes are recalculated and the frontend must respect that.
	 * Returns the full mapped data on success, or throws on failure so callers
	 * can handle it (e.g., show an error message / retry button).
	 */
	const fetchApplication = useCallback(
		async (id: string): Promise<Partial<ApplicationData>> => {
			const response = await apiService.application.get(id);
			const mappedData = mapResponseToApplicationData(response);

			// Quote selection fields should ALWAYS be applied (even if null)
			// because the backend clears them when quotes are recalculated
			const quoteSelectionFields = [
				'selected_sick_leave_short_term_quote_id',
				'selected_occupational_health_service_quote_id',
				'selected_wga_gap_extended_quote_id',
				'selected_wga_gap_quote_id',
				'selected_wia_excess_quote_id'
			];

			// Filter out null values EXCEPT for quote selection fields
			const dataToMerge = Object.fromEntries(
				Object.entries(mappedData).filter(
					([key, value]) => value != null || quoteSelectionFields.includes(key)
				)
			);

			setApplicationData((prevData) => ({
				...prevData,
				...dataToMerge
			}));
			return mappedData;
		},
		[setApplicationData]
	);

	const saveApplication = useCallback(
		async (id: string, applicationData: Partial<ApplicationData>) => {
			await apiService.application.update(id, applicationData);
		},
		[]
	);

	const submitApplication = useCallback(async (id: string) => {
		await apiService.application.submit(id);
	}, []);

	return {
		fetch: fetchApplication,
		update: updateApplication,
		save: saveApplication,
		submit: submitApplication
	};
}
